// src/pages/MeetingsReferenceData.tsx
//
// Investment-team gated admin surface for the meeting reference-data tables that back
// the native meeting forms (MeetingRequest / MeetingBooked):
//   • meeting_house_contacts — BDM + fund-house contacts (small, ~100 rows) → full CRUD.
//   • meeting_fund_lookup     — the fund catalogue (THOUSANDS of rows) → search-only CRUD.
// A third tab drives the `meeting-sheet-import` edge fn (Google Sheets → these tables)
// with baked presets, a dry-run toggle, a peek panel and a free-form sync/peek body.
//
// RLS already lets Investment authors read+write both tables, so CRUD uses the plain
// supabase client (no edge fn). The tables are not in the generated types yet, hence the
// `(supabase as any)` casts. The import fn runs under the caller's JWT + their Google token
// server-side; the browser only posts the config.

import { useEffect, useState } from "react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Pencil, Plus, RefreshCw, Search, Trash2 } from "lucide-react";

// ---------------------------------------------------------------------------
// Types (tables are not in the generated Database types yet)
// ---------------------------------------------------------------------------
type HouseContact = {
  id: string;
  fund_house_name: string | null;
  contact_type: string | null; // 'bdm' | 'fund_house'
  ordinal: number | null;
  forename: string | null;
  surname: string | null;
  full_name: string | null;
  email: string | null;
  is_active: boolean | null;
  notes: string | null;
  source: string | null;
};

type FundLookup = {
  id: string;
  isin: string | null;
  sedol: string | null;
  fund_name: string | null;
  fund_house: string | null;
  fund_class: string | null;
  inc_acc: string | null;
  amc: number | null;
  ter_pct: number | null;
  available_on_abrdn: boolean | null;
  is_active: boolean | null;
  source: string | null;
};

// Editable form shapes keep numeric/optional fields as strings for controlled inputs.
type ContactForm = {
  fund_house_name: string;
  contact_type: string;
  ordinal: string;
  forename: string;
  surname: string;
  full_name: string;
  email: string;
  is_active: boolean;
  notes: string;
  source: string;
};

type FundForm = {
  isin: string;
  sedol: string;
  fund_name: string;
  fund_house: string;
  fund_class: string;
  inc_acc: string;
  amc: string;
  ter_pct: string;
  available_on_abrdn: boolean;
  is_active: boolean;
  source: string;
};

const emptyContact: ContactForm = {
  fund_house_name: "", contact_type: "fund_house", ordinal: "1", forename: "", surname: "",
  full_name: "", email: "", is_active: true, notes: "", source: "",
};

const emptyFund: FundForm = {
  isin: "", sedol: "", fund_name: "", fund_house: "", fund_class: "", inc_acc: "",
  amc: "", ter_pct: "", available_on_abrdn: false, is_active: true, source: "",
};

const HOUSE_CONTACTS = "meeting_house_contacts";
const FUND_LOOKUP = "meeting_fund_lookup";

// ---------------------------------------------------------------------------
// Import presets (baked config posted to the meeting-sheet-import edge fn)
// ---------------------------------------------------------------------------
const PRESET_ABRDN_FUNDS = {
  mode: "sync",
  spreadsheet_id: "1kSZ_-0ZW4Zd1qKlDxATnvJKvk80AX1WWZ07jl2kFXzQ",
  range: "Sheet1",
  target_table: "meeting_fund_lookup",
  column_map: {
    "Fund Manager": "fund_house",
    "Fund Name": "fund_name",
    "Sedol": "sedol",
    "ISIN": "isin",
    "Inc / Acc": "inc_acc",
    "AMC (%)": "amc",
    "OCF (%)": "ter_pct",
  },
  key_columns: ["isin"],
  numeric_columns: ["amc", "ter_pct"],
  upper_columns: ["isin"],
  raw_column: "raw",
  constants: { available_on_abrdn: true, is_active: true, source: "sheet:Abrdn_DFM_Fund_List" },
};

const PRESET_HOUSE_CONTACTS = {
  mode: "sync",
  spreadsheet_id: "17uJgZ2GhBKuTKWm7ylv6r6Vc2v06bC4REth2dMKIXLU",
  range: "A1:I500",
  target_table: "meeting_house_contacts",
  column_map: {
    "Fund House": "fund_house_name",
    "Contact": "email",
    "Contact 1 Forename": "forename",
    "Contact 1 Surname": "surname",
    "Contact 1 Combined Name": "full_name",
  },
  key_columns: ["fund_house_name", "contact_type", "ordinal"],
  constants: { contact_type: "fund_house", ordinal: 1, is_active: true, source: "sheet:CBAM Fund House Contacts" },
};

// BDM Contacts sheet — header names unknown, so this is a peek-then-custom flow.
const BDM_SPREADSHEET_ID = "1FgpRwuechXiUz5U-VDudtz_Vde2FC0DZNUTQQnurdPk";
const BDM_RANGE = "'BDM Contacts'!A1:Z500";

// Editable template for the custom sync/peek body (pre-seeded for the BDM sheet).
const CUSTOM_TEMPLATE = JSON.stringify(
  {
    mode: "sync",
    spreadsheet_id: BDM_SPREADSHEET_ID,
    range: BDM_RANGE,
    target_table: "meeting_house_contacts",
    column_map: {
      "<Sheet header for fund house>": "fund_house_name",
      "<Sheet header for BDM name>": "full_name",
      "<Sheet header for email>": "email",
    },
    key_columns: ["fund_house_name", "contact_type", "ordinal"],
    constants: { contact_type: "bdm", ordinal: 1, is_active: true, source: "sheet:BDM Contacts" },
  },
  null,
  2,
);

const MeetingsReferenceData = () => {
  const { toast } = useToast();

  // ---- Tab 1: house contacts ----------------------------------------------
  const [contacts, setContacts] = useState<HouseContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactEditingId, setContactEditingId] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState<ContactForm>(emptyContact);
  const [savingContact, setSavingContact] = useState(false);

  // ---- Tab 2: fund lookup --------------------------------------------------
  const [fundQuery, setFundQuery] = useState("");
  const [funds, setFunds] = useState<FundLookup[]>([]);
  const [fundsLoading, setFundsLoading] = useState(false);
  const [fundSearched, setFundSearched] = useState(false);
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const [fundEditingId, setFundEditingId] = useState<string | null>(null);
  const [fundForm, setFundForm] = useState<FundForm>(emptyFund);
  const [savingFund, setSavingFund] = useState(false);

  // ---- Tab 3: import -------------------------------------------------------
  const [dryRun, setDryRun] = useState(true);
  const [importRunning, setImportRunning] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [peekSpreadsheetId, setPeekSpreadsheetId] = useState(BDM_SPREADSHEET_ID);
  const [peekRange, setPeekRange] = useState(BDM_RANGE);
  const [peekRunning, setPeekRunning] = useState(false);
  const [peekResult, setPeekResult] = useState<any>(null);
  const [customBody, setCustomBody] = useState(CUSTOM_TEMPLATE);
  const [customRunning, setCustomRunning] = useState(false);
  const [customResult, setCustomResult] = useState<any>(null);

  // ==========================================================================
  // House contacts CRUD
  // ==========================================================================
  const loadContacts = async () => {
    setContactsLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from(HOUSE_CONTACTS)
        .select("*")
        .order("fund_house_name")
        .order("contact_type")
        .order("ordinal");
      if (error) throw error;
      setContacts((data ?? []) as HouseContact[]);
    } catch (e: any) {
      toast({ title: "Couldn't load house contacts", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setContactsLoading(false);
    }
  };

  useEffect(() => { void loadContacts(); }, []);

  const openAddContact = () => {
    setContactEditingId(null);
    setContactForm(emptyContact);
    setContactDialogOpen(true);
  };

  const openEditContact = (row: HouseContact) => {
    setContactEditingId(row.id);
    setContactForm({
      fund_house_name: row.fund_house_name ?? "",
      contact_type: row.contact_type ?? "fund_house",
      ordinal: row.ordinal == null ? "" : String(row.ordinal),
      forename: row.forename ?? "",
      surname: row.surname ?? "",
      full_name: row.full_name ?? "",
      email: row.email ?? "",
      is_active: row.is_active ?? true,
      notes: row.notes ?? "",
      source: row.source ?? "",
    });
    setContactDialogOpen(true);
  };

  const saveContact = async () => {
    if (!contactForm.fund_house_name.trim()) {
      toast({ title: "Fund house name is required", variant: "destructive" }); return;
    }
    if (contactForm.ordinal !== "" && Number.isNaN(Number(contactForm.ordinal))) {
      toast({ title: "Ordinal must be a number", variant: "destructive" }); return;
    }
    const payload = {
      fund_house_name: contactForm.fund_house_name.trim(),
      contact_type: contactForm.contact_type,
      ordinal: contactForm.ordinal === "" ? null : Number(contactForm.ordinal),
      forename: contactForm.forename.trim() || null,
      surname: contactForm.surname.trim() || null,
      full_name: contactForm.full_name.trim() || null,
      email: contactForm.email.trim() || null,
      is_active: contactForm.is_active,
      notes: contactForm.notes.trim() || null,
      source: contactForm.source.trim() || null,
    };
    setSavingContact(true);
    try {
      const q = contactEditingId
        ? (supabase as any).from(HOUSE_CONTACTS).update(payload).eq("id", contactEditingId)
        : (supabase as any).from(HOUSE_CONTACTS).insert(payload);
      const { error } = await q;
      if (error) throw error;
      toast({ title: contactEditingId ? "Contact updated" : "Contact added" });
      setContactDialogOpen(false);
      await loadContacts();
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setSavingContact(false);
    }
  };

  const deleteContact = async (id: string) => {
    try {
      const { error } = await (supabase as any).from(HOUSE_CONTACTS).delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Contact deleted" });
      await loadContacts();
    } catch (e: any) {
      toast({ title: "Delete failed", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  // ==========================================================================
  // Fund lookup CRUD (search-driven — never loads the whole table)
  // ==========================================================================
  const searchFunds = async () => {
    const raw = fundQuery.trim();
    if (!raw) {
      setFunds([]); setFundSearched(false);
      toast({ title: "Enter a search term", description: "Search by fund name or ISIN.", variant: "destructive" });
      return;
    }
    // Strip characters that would break PostgREST's .or() filter grammar.
    const q = raw.replace(/[,()]/g, " ").trim();
    setFundsLoading(true);
    try {
      const { data, error } = await (supabase as any)
        .from(FUND_LOOKUP)
        .select("*")
        .or(`fund_name.ilike.%${q}%,isin.ilike.%${q}%`)
        .limit(50);
      if (error) throw error;
      setFunds((data ?? []) as FundLookup[]);
      setFundSearched(true);
    } catch (e: any) {
      toast({ title: "Search failed", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setFundsLoading(false);
    }
  };

  const openAddFund = () => {
    setFundEditingId(null);
    setFundForm(emptyFund);
    setFundDialogOpen(true);
  };

  const openEditFund = (row: FundLookup) => {
    setFundEditingId(row.id);
    setFundForm({
      isin: row.isin ?? "",
      sedol: row.sedol ?? "",
      fund_name: row.fund_name ?? "",
      fund_house: row.fund_house ?? "",
      fund_class: row.fund_class ?? "",
      inc_acc: row.inc_acc ?? "",
      amc: row.amc == null ? "" : String(row.amc),
      ter_pct: row.ter_pct == null ? "" : String(row.ter_pct),
      available_on_abrdn: row.available_on_abrdn ?? false,
      is_active: row.is_active ?? true,
      source: row.source ?? "",
    });
    setFundDialogOpen(true);
  };

  const saveFund = async () => {
    if (!fundForm.fund_name.trim()) {
      toast({ title: "Fund name is required", variant: "destructive" }); return;
    }
    if (fundForm.amc !== "" && Number.isNaN(Number(fundForm.amc))) {
      toast({ title: "AMC must be a number", variant: "destructive" }); return;
    }
    if (fundForm.ter_pct !== "" && Number.isNaN(Number(fundForm.ter_pct))) {
      toast({ title: "TER must be a number", variant: "destructive" }); return;
    }
    const payload = {
      isin: fundForm.isin.trim().toUpperCase() || null,
      sedol: fundForm.sedol.trim() || null,
      fund_name: fundForm.fund_name.trim(),
      fund_house: fundForm.fund_house.trim() || null,
      fund_class: fundForm.fund_class.trim() || null,
      inc_acc: fundForm.inc_acc.trim() || null,
      amc: fundForm.amc === "" ? null : Number(fundForm.amc),
      ter_pct: fundForm.ter_pct === "" ? null : Number(fundForm.ter_pct),
      available_on_abrdn: fundForm.available_on_abrdn,
      is_active: fundForm.is_active,
      source: fundForm.source.trim() || null,
    };
    setSavingFund(true);
    try {
      const q = fundEditingId
        ? (supabase as any).from(FUND_LOOKUP).update(payload).eq("id", fundEditingId)
        : (supabase as any).from(FUND_LOOKUP).insert(payload);
      const { error } = await q;
      if (error) throw error;
      toast({ title: fundEditingId ? "Fund updated" : "Fund added" });
      setFundDialogOpen(false);
      if (fundSearched) await searchFunds();
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setSavingFund(false);
    }
  };

  const deleteFund = async (id: string) => {
    try {
      const { error } = await (supabase as any).from(FUND_LOOKUP).delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Fund deleted" });
      setFunds((prev) => prev.filter((f) => f.id !== id));
    } catch (e: any) {
      toast({ title: "Delete failed", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  // ==========================================================================
  // Import (meeting-sheet-import edge fn)
  // ==========================================================================
  const withDryRun = (body: Record<string, any>) => (dryRun ? { ...body, dry_run: true } : body);

  // supabase.functions.invoke() collapses any non-2xx into "non-2xx status code";
  // the real reason lives in the response body, so read it back out.
  const readFnError = async (error: any): Promise<{ message: string; body?: any }> => {
    try {
      const res = (error as any)?.context;
      if (res && typeof res.json === "function") {
        const body = await res.clone().json();
        const msg = body?.detail ?? body?.error ?? error?.message ?? "Unknown error";
        return { message: String(msg), body };
      }
    } catch { /* fall through to the generic message */ }
    return { message: error?.message ?? String(error) };
  };

  const runImport = async (body: Record<string, any>) => {
    setImportRunning(true);
    setImportResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("meeting-sheet-import", { body: withDryRun(body) });
      if (error) {
        const { message, body: errBody } = await readFnError(error);
        setImportResult(errBody ?? { ok: false, error: message });
        toast({ title: "Import failed", description: message, variant: "destructive" });
        return;
      }
      setImportResult(data);
      if (!dryRun && data?.ok !== false && data?.target_table === HOUSE_CONTACTS) {
        await loadContacts();
      }
      const summary = data && typeof data === "object"
        ? `upserted ${data.upserted ?? 0}, skipped ${data.skipped ?? 0} of ${data.read ?? 0}`
        : "See result below.";
      toast({
        title: (data && data.ok === false) ? "Import returned an error" : (dryRun ? "Dry run complete" : "Import complete"),
        description: summary,
        variant: (data && data.ok === false) ? "destructive" : "default",
      });
    } catch (e: any) {
      setImportResult({ ok: false, error: e?.message ?? String(e) });
      toast({ title: "Import failed", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setImportRunning(false);
    }
  };

  const runPeek = async () => {
    if (!peekSpreadsheetId.trim() || !peekRange.trim()) {
      toast({ title: "Spreadsheet ID and range are required", variant: "destructive" }); return;
    }
    setPeekRunning(true);
    setPeekResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("meeting-sheet-import", {
        body: { mode: "peek", spreadsheet_id: peekSpreadsheetId.trim(), range: peekRange.trim() },
      });
      if (error) {
        const { message, body: errBody } = await readFnError(error);
        setPeekResult(errBody ?? { ok: false, error: message });
        toast({ title: "Peek failed", description: message, variant: "destructive" });
        return;
      }
      setPeekResult(data);
    } catch (e: any) {
      setPeekResult({ ok: false, error: e?.message ?? String(e) });
      toast({ title: "Peek failed", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setPeekRunning(false);
    }
  };

  const runCustom = async () => {
    let body: Record<string, any>;
    try {
      body = JSON.parse(customBody);
    } catch (e: any) {
      toast({ title: "Invalid JSON", description: e?.message ?? "Check the body syntax.", variant: "destructive" });
      return;
    }
    if (!body || typeof body !== "object") {
      toast({ title: "Body must be a JSON object", variant: "destructive" }); return;
    }
    setCustomRunning(true);
    setCustomResult(null);
    try {
      // A 'peek' body is read-only; only inject dry_run for writes (sync).
      const finalBody = body.mode === "peek" ? body : withDryRun(body);
      const { data, error } = await supabase.functions.invoke("meeting-sheet-import", { body: finalBody });
      if (error) {
        setCustomResult({ ok: false, error: error.message ?? String(error) });
        toast({ title: "Request failed", description: error.message, variant: "destructive" });
        return;
      }
      setCustomResult(data);
      toast({ title: (data && data.ok === false) ? "Returned an error" : "Request complete", variant: (data && data.ok === false) ? "destructive" : "default" });
    } catch (e: any) {
      setCustomResult({ ok: false, error: e?.message ?? String(e) });
      toast({ title: "Request failed", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setCustomRunning(false);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 py-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Meetings Reference Data — CBAM"
        description="Manage the fund-house contacts and fund lookup reference tables that back the Cape Berkshire Asset Management meeting forms."
        path="/meetings/reference-data"
        noindex
      />
      <div className="max-w-6xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Meetings Reference Data</CardTitle>
            <CardDescription>
              Maintain the reference tables that power the meeting forms: fund-house / BDM contacts,
              the fund lookup catalogue, and the Google Sheets importer.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="contacts">
              <TabsList className="mb-6">
                <TabsTrigger value="contacts">House Contacts</TabsTrigger>
                <TabsTrigger value="funds">Fund Lookup</TabsTrigger>
                <TabsTrigger value="import">Import from Google Sheets</TabsTrigger>
              </TabsList>

              {/* ============================ TAB 1 ============================ */}
              <TabsContent value="contacts">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-muted-foreground">
                    {contactsLoading ? "Loading…" : `${contacts.length} contact(s)`}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => void loadContacts()} disabled={contactsLoading}>
                      <RefreshCw className={`mr-2 h-4 w-4 ${contactsLoading ? "animate-spin" : ""}`} />
                      Refresh
                    </Button>
                    <Button size="sm" onClick={openAddContact}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add contact
                    </Button>
                  </div>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fund House</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="w-16">Ord.</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="w-20">Active</TableHead>
                        <TableHead className="w-24 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!contactsLoading && contacts.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                            No contacts yet. Add one, or import from the Fund House Contacts sheet.
                          </TableCell>
                        </TableRow>
                      )}
                      {contacts.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">{c.fund_house_name ?? "—"}</TableCell>
                          <TableCell>{c.contact_type ?? "—"}</TableCell>
                          <TableCell>{c.ordinal ?? "—"}</TableCell>
                          <TableCell>{c.full_name || [c.forename, c.surname].filter(Boolean).join(" ") || "—"}</TableCell>
                          <TableCell>{c.email ?? "—"}</TableCell>
                          <TableCell>{c.is_active ? "Yes" : "No"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditContact(c)} aria-label="Edit">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" aria-label="Delete">
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this contact?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {c.fund_house_name} · {c.full_name ?? c.email ?? c.contact_type}. This can't be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => void deleteContact(c.id)}>Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* ============================ TAB 2 ============================ */}
              <TabsContent value="funds">
                <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  This table holds thousands of funds — it is never loaded in full. Search by fund name
                  or ISIN to find and edit rows (max 50 results).
                </div>
                <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                    <Input
                      value={fundQuery}
                      onChange={(e) => setFundQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") void searchFunds(); }}
                      placeholder="Search fund name or ISIN…"
                    />
                    <Button size="sm" onClick={() => void searchFunds()} disabled={fundsLoading}>
                      {fundsLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                      Search
                    </Button>
                  </div>
                  <Button size="sm" variant="outline" onClick={openAddFund}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add fund
                  </Button>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fund Name</TableHead>
                        <TableHead>Fund House</TableHead>
                        <TableHead>ISIN</TableHead>
                        <TableHead className="w-20">Inc/Acc</TableHead>
                        <TableHead className="w-20">AMC</TableHead>
                        <TableHead className="w-20">TER %</TableHead>
                        <TableHead className="w-20">Active</TableHead>
                        <TableHead className="w-24 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!fundsLoading && fundSearched && funds.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                            No funds match “{fundQuery}”.
                          </TableCell>
                        </TableRow>
                      )}
                      {!fundSearched && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                            Search above to list matching funds.
                          </TableCell>
                        </TableRow>
                      )}
                      {funds.map((f) => (
                        <TableRow key={f.id}>
                          <TableCell className="font-medium">{f.fund_name ?? "—"}</TableCell>
                          <TableCell>{f.fund_house ?? "—"}</TableCell>
                          <TableCell>{f.isin ?? "—"}</TableCell>
                          <TableCell>{f.inc_acc ?? "—"}</TableCell>
                          <TableCell>{f.amc ?? "—"}</TableCell>
                          <TableCell>{f.ter_pct ?? "—"}</TableCell>
                          <TableCell>{f.is_active ? "Yes" : "No"}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditFund(f)} aria-label="Edit">
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="icon" aria-label="Delete">
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete this fund?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {f.fund_name} {f.isin ? `· ${f.isin}` : ""}. This can't be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => void deleteFund(f.id)}>Delete</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* ============================ TAB 3 ============================ */}
              <TabsContent value="import">
                <div className="space-y-6">
                  <div className="flex items-center gap-2 rounded-md border p-3">
                    <Checkbox
                      id="dry-run"
                      checked={dryRun}
                      onCheckedChange={(c) => setDryRun(c as boolean)}
                    />
                    <Label htmlFor="dry-run" className="cursor-pointer">
                      Dry run (preview only — reads the sheet and reports what would change, without writing)
                    </Label>
                  </div>

                  {/* Presets */}
                  <section className="space-y-3">
                    <h3 className="text-sm font-semibold">Presets</h3>
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => void runImport(PRESET_ABRDN_FUNDS)} disabled={importRunning}>
                        {importRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Import Abrdn fund list → Fund Lookup
                      </Button>
                      <Button variant="secondary" onClick={() => void runImport(PRESET_HOUSE_CONTACTS)} disabled={importRunning}>
                        {importRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Import Fund House Contacts → House Contacts
                      </Button>
                    </div>
                    {importResult && (
                      <pre className="max-h-72 overflow-auto rounded-md border bg-muted/40 p-3 text-xs">
                        {JSON.stringify(importResult, null, 2)}
                      </pre>
                    )}
                  </section>

                  {/* Peek */}
                  <section className="space-y-3 rounded-md border p-4">
                    <div>
                      <h3 className="text-sm font-semibold">Peek a sheet</h3>
                      <p className="text-xs text-muted-foreground">
                        Read a sheet's headers + sample rows without writing. Pre-filled with the BDM
                        Contacts sheet — peek it to discover the column names, then build a custom sync below.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Spreadsheet ID</Label>
                        <Input value={peekSpreadsheetId} onChange={(e) => setPeekSpreadsheetId(e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Range</Label>
                        <Input value={peekRange} onChange={(e) => setPeekRange(e.target.value)} />
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => void runPeek()} disabled={peekRunning}>
                      {peekRunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                      Peek
                    </Button>
                    {peekResult && (
                      <pre className="max-h-72 overflow-auto rounded-md border bg-muted/40 p-3 text-xs">
                        {JSON.stringify(peekResult, null, 2)}
                      </pre>
                    )}
                  </section>

                  {/* Custom */}
                  <section className="space-y-3 rounded-md border p-4">
                    <div>
                      <h3 className="text-sm font-semibold">Custom sync / peek</h3>
                      <p className="text-xs text-muted-foreground">
                        Edit the JSON body and post it directly — use this to import the BDM Contacts sheet
                        (or any future sheet) once you know its column names. Set <code>"mode":"peek"</code> to
                        preview, or <code>"mode":"sync"</code> to write. The dry-run toggle above applies to sync bodies.
                      </p>
                    </div>
                    <Textarea
                      value={customBody}
                      onChange={(e) => setCustomBody(e.target.value)}
                      rows={16}
                      spellCheck={false}
                      className="font-mono text-xs"
                    />
                    <Button variant="outline" size="sm" onClick={() => void runCustom()} disabled={customRunning}>
                      {customRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Post body
                    </Button>
                    {customResult && (
                      <pre className="max-h-72 overflow-auto rounded-md border bg-muted/40 p-3 text-xs">
                        {JSON.stringify(customResult, null, 2)}
                      </pre>
                    )}
                  </section>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* ===================== House-contact add/edit dialog ===================== */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{contactEditingId ? "Edit contact" : "Add contact"}</DialogTitle>
            <DialogDescription>Fund-house or BDM contact used by the meeting forms.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-sm">Fund house name *</Label>
              <Input
                value={contactForm.fund_house_name}
                onChange={(e) => setContactForm({ ...contactForm, fund_house_name: e.target.value })}
                placeholder="e.g. Baillie Gifford"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Contact type</Label>
                <Select value={contactForm.contact_type} onValueChange={(v) => setContactForm({ ...contactForm, contact_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fund_house">fund_house</SelectItem>
                    <SelectItem value="bdm">bdm</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Ordinal</Label>
                <Input
                  type="number"
                  value={contactForm.ordinal}
                  onChange={(e) => setContactForm({ ...contactForm, ordinal: e.target.value })}
                  placeholder="1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Forename</Label>
                <Input value={contactForm.forename} onChange={(e) => setContactForm({ ...contactForm, forename: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm">Surname</Label>
                <Input value={contactForm.surname} onChange={(e) => setContactForm({ ...contactForm, surname: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-sm">Full name</Label>
              <Input value={contactForm.full_name} onChange={(e) => setContactForm({ ...contactForm, full_name: e.target.value })} placeholder="Overrides forename + surname if set" />
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <Input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
            </div>
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} rows={2} />
            </div>
            <div>
              <Label className="text-sm">Source</Label>
              <Input value={contactForm.source} onChange={(e) => setContactForm({ ...contactForm, source: e.target.value })} placeholder="e.g. manual" />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="contact-active"
                checked={contactForm.is_active}
                onCheckedChange={(c) => setContactForm({ ...contactForm, is_active: c as boolean })}
              />
              <Label htmlFor="contact-active" className="cursor-pointer">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)} disabled={savingContact}>Cancel</Button>
            <Button onClick={() => void saveContact()} disabled={savingContact}>
              {savingContact && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {contactEditingId ? "Save changes" : "Add contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================== Fund add/edit dialog ===================== */}
      <Dialog open={fundDialogOpen} onOpenChange={setFundDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{fundEditingId ? "Edit fund" : "Add fund"}</DialogTitle>
            <DialogDescription>A row in the fund lookup catalogue.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-sm">Fund name *</Label>
              <Input value={fundForm.fund_name} onChange={(e) => setFundForm({ ...fundForm, fund_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-sm">Fund house</Label>
              <Input value={fundForm.fund_house} onChange={(e) => setFundForm({ ...fundForm, fund_house: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">ISIN</Label>
                <Input value={fundForm.isin} onChange={(e) => setFundForm({ ...fundForm, isin: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm">SEDOL</Label>
                <Input value={fundForm.sedol} onChange={(e) => setFundForm({ ...fundForm, sedol: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Fund class</Label>
                <Input value={fundForm.fund_class} onChange={(e) => setFundForm({ ...fundForm, fund_class: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm">Inc / Acc</Label>
                <Input value={fundForm.inc_acc} onChange={(e) => setFundForm({ ...fundForm, inc_acc: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">AMC</Label>
                <Input type="number" step="any" value={fundForm.amc} onChange={(e) => setFundForm({ ...fundForm, amc: e.target.value })} />
              </div>
              <div>
                <Label className="text-sm">TER %</Label>
                <Input type="number" step="any" value={fundForm.ter_pct} onChange={(e) => setFundForm({ ...fundForm, ter_pct: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-sm">Source</Label>
              <Input value={fundForm.source} onChange={(e) => setFundForm({ ...fundForm, source: e.target.value })} placeholder="e.g. manual" />
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="fund-abrdn"
                  checked={fundForm.available_on_abrdn}
                  onCheckedChange={(c) => setFundForm({ ...fundForm, available_on_abrdn: c as boolean })}
                />
                <Label htmlFor="fund-abrdn" className="cursor-pointer">Available on Abrdn</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="fund-active"
                  checked={fundForm.is_active}
                  onCheckedChange={(c) => setFundForm({ ...fundForm, is_active: c as boolean })}
                />
                <Label htmlFor="fund-active" className="cursor-pointer">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFundDialogOpen(false)} disabled={savingFund}>Cancel</Button>
            <Button onClick={() => void saveFund()} disabled={savingFund}>
              {savingFund && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {fundEditingId ? "Save changes" : "Add fund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingsReferenceData;
